import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { computeQuote, renderTemplate, generateClientAlcoholList, generateInternalSupplies, validateQuoteInputs, hoursFromTimes, suppliesFormula, ADDON_PRESETS, alcoholListApplicability } from "@/lib/quote";
import {
  CAD, statusTone, EVENT_STATUSES, CONTRACT_STATUSES, PAYMENT_STATUSES, MENU_STATUSES, DOC_STATUSES,
  MENU_SELECTION_STATUSES, ALCOHOL_DISCLAIMER,
} from "@/lib/constants";
import { DocumentModal } from "@/components/documents/Documents";
import { DeleteConfirmDialog } from "@/components/admin/DeleteConfirmDialog";
import { isProtectedEvent } from "@/pages/admin/Events";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  ArrowLeft, Phone, Mail, FileText, ScrollText, Receipt, Utensils, Baby, Wine, PackageCheck,
  Calculator, Send, Save, Zap, Clock, Plus, CheckCircle2, CreditCard, BellRing, PenLine, Copy, Link2,
  AlertTriangle, Trash2, Beer, ListChecks, Archive, ArchiveRestore,
} from "lucide-react";
import { toast } from "sonner";

export default function EventDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [transport, setTransport] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [emailConfigured, setEmailConfigured] = useState(false);
  const [doc, setDoc] = useState(null);
  const [note, setNote] = useState("");
  const [delOpen, setDelOpen] = useState(false);

  const load = () => api.get(`/events/${id}`).then((r) => setEvent(r.data)).catch(() => navigate("/admin/events"));
  useEffect(() => {
    load();
    api.get("/settings/pricing").then((r) => setPricing(r.data));
    api.get("/settings/transportation").then((r) => setTransport(r.data));
    api.get("/templates").then((r) => setTemplates(r.data));
    api.get("/email/status").then((r) => setEmailConfigured(r.data.configured));
  }, [id]);

  const patch = async (updates, msg) => {
    const { data } = await api.patch(`/events/${id}`, updates);
    setEvent(data);
    if (msg) toast.success(msg);
    return data;
  };

  const trigger = async (action) => {
    try {
      const { data } = await api.post(`/events/${id}/webhook/${action}`);
      if (data.status === "success" || data.status === "notified") toast.success("Owner notified");
      else if (data.status === "skipped") toast.info("No automation configured for this action");
      else toast.error("Notification failed — check the log");
    } catch { toast.error("Notification error"); }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    const { data } = await api.post(`/events/${id}/timeline`, { type: "note", text: note });
    setEvent(data); setNote("");
  };

  const toggleArchive = async () => {
    const next = !event.archived;
    await api.patch(`/events/${id}/archive`, { archived: next });
    toast.success(next ? "Event archived" : "Event restored");
    load();
  };

  const removeEvent = async () => {
    await api.delete(`/events/${id}`);
    toast.success("Event permanently deleted");
    navigate("/admin/events");
  };

  if (!event) return <div className="animate-pulse text-muted-foreground text-sm">Loading…</div>;

  return (
    <div className="animate-fade-in">
      <button onClick={() => navigate("/admin/events")} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-ink mb-4" data-testid="back-to-events"><ArrowLeft className="h-4 w-4" /> All events</button>

      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="font-serif text-4xl">{event.full_name}</h1>
          <p className="text-muted-foreground">{event.event_type} · {event.event_city} · {event.event_date || "Date TBD"}</p>
          <div className="mt-2 flex flex-wrap gap-2">
            <Badge variant="outline" className={statusTone(event.quote_status || "Draft")} data-testid="quote-status-badge">Quote: {event.quote_status || "Draft"}</Badge>
            {event.client_choices && <Badge variant="outline" className="bg-olive/10 text-olive border-olive/30" data-testid="drink-choices-badge">Drink choices received</Badge>}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {event.phone && <a href={`tel:${event.phone}`} className="btn-outline text-xs py-2 px-4" data-testid="event-call"><Phone className="h-3 w-3" /> Call</a>}
          <a href={`mailto:${event.email}`} className="btn-outline text-xs py-2 px-4" data-testid="event-email"><Mail className="h-3 w-3" /> Email</a>
          <button onClick={toggleArchive} className="btn-outline text-xs py-2 px-4" data-testid="event-archive-toggle">
            {event.archived ? <><ArchiveRestore className="h-3 w-3" /> Restore</> : <><Archive className="h-3 w-3" /> Archive</>}
          </button>
          <button onClick={() => setDelOpen(true)} className="btn-outline text-xs py-2 px-4 text-destructive border-destructive/30 hover:bg-destructive/10" data-testid="event-delete-btn"><Trash2 className="h-3 w-3" /> Delete</button>
        </div>
      </div>

      {event.archived && (
        <div className="rounded-xl bg-clay text-muted-foreground p-3 text-sm mb-6 flex items-center gap-2" data-testid="event-archived-banner">
          <Archive className="h-4 w-4" /> This event is archived — it's hidden from the dashboard and active lists. Restore it to make it active again.
        </div>
      )}

      {/* Status strip */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
        <StatusSelect label="Event" value={event.event_status} options={EVENT_STATUSES} onChange={(v) => { patch({ event_status: v }, "Status updated"); if (v === "Confirmed") trigger("event_confirmed"); if (v === "Completed") trigger("event_completed"); }} testid="event-status" />
        <StatusSelect label="Contract" value={event.contract_status} options={CONTRACT_STATUSES} onChange={(v) => patch({ contract_status: v })} testid="contract-status" />
        <StatusSelect label="Payment" value={event.payment_status} options={PAYMENT_STATUSES} onChange={(v) => patch({ payment_status: v })} testid="payment-status" />
        <StatusSelect label="Menu" value={event.menu_status} options={MENU_STATUSES} onChange={(v) => patch({ menu_status: v })} testid="menu-status" />
        <StatusSelect label="Menu Selection" value={event.menu_selection_status || "Not sent"} options={MENU_SELECTION_STATUSES} onChange={(v) => patch({ menu_selection_status: v }, "Menu selection updated")} testid="menu-selection-status" />
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="flex flex-wrap h-auto bg-clay p-1 rounded-xl mb-6">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="quote" data-testid="tab-quote">Quote</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
          <TabsTrigger value="menu" data-testid="tab-menu">Menu</TabsTrigger>
          <TabsTrigger value="alcohol" data-testid="tab-alcohol">Alcohol & Supplies</TabsTrigger>
          <TabsTrigger value="checklist" data-testid="tab-checklist">Checklist</TabsTrigger>
          <TabsTrigger value="comms" data-testid="tab-comms">Communication</TabsTrigger>
        </TabsList>

        <TabsContent value="overview"><OverviewTab event={event} patch={patch} note={note} setNote={setNote} addNote={addNote} /></TabsContent>
        <TabsContent value="quote"><QuoteTab event={event} pricing={pricing} transport={transport} patch={patch} trigger={trigger} openDoc={() => setDoc("quote")} reload={load} /></TabsContent>
        <TabsContent value="documents"><DocumentsTab event={event} patch={patch} trigger={trigger} setDoc={setDoc} reload={load} /></TabsContent>
        <TabsContent value="menu"><MenuTab event={event} patch={patch} trigger={trigger} setDoc={setDoc} /></TabsContent>
        <TabsContent value="alcohol"><AlcoholSuppliesTab event={event} patch={patch} trigger={trigger} setDoc={setDoc} /></TabsContent>
        <TabsContent value="checklist"><ChecklistTab event={event} patch={patch} /></TabsContent>
        <TabsContent value="comms"><CommsTab event={event} templates={templates} emailConfigured={emailConfigured} /></TabsContent>
      </Tabs>

      <DocumentModal open={!!doc} onClose={() => setDoc(null)} type={doc} event={event} pricing={pricing}
        squareLink={event.invoice?.square_link}
        onPayDeposit={() => patch({ payment_status: "Pending confirmation" }, "Marked as pending confirmation")} />

      <DeleteConfirmDialog
        open={delOpen}
        onOpenChange={setDelOpen}
        onConfirm={removeEvent}
        description="Are you sure you want to permanently delete this event and all related records (quote, contract, invoice, menu, alcohol list, supplies, checklist, CRM timeline, related webhook logs, and the originating lead)? This action cannot be undone."
        protectedWarning={isProtectedEvent(event) ? "This event appears active or confirmed. Please archive it first or change the status before deleting." : ""}
      />
    </div>
  );
}

const StatusSelect = ({ label, value, options, onChange, testid }) => (
  <div className="df-card p-3">
    <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">{label}</div>
    <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full bg-transparent text-sm font-medium outline-none" data-testid={testid}>
      {options.map((o) => <option key={o}>{o}</option>)}
    </select>
  </div>
);

/* ---------- OVERVIEW ---------- */
function OverviewTab({ event, patch, note, setNote, addNote }) {
  const [f, setF] = useState(event);
  useEffect(() => setF(event), [event.id]);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const fields = [
    ["email", "Email"], ["phone", "Phone"], ["event_date", "Event date", "date"], ["event_city", "City"],
    ["event_address", "Address"], ["event_type", "Event type"], ["indoor_outdoor", "Indoor/Outdoor"],
    ["package_type", "Package type"], ["guest_count", "Guests", "number"], ["adult_count", "Adults", "number"],
    ["kids_count", "Kids/Teens", "number"], ["service_hours", "Service hours", "number"],
    ["service_start_time", "Service start", "time"], ["service_end_time", "Service end", "time"],
    ["setup_arrival_time", "Setup arrival", "time"], ["service_type", "Service type"], ["theme_colors", "Theme/colors"],
    ["allergies", "Allergies"],
  ];
  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 df-card p-6">
        <h3 className="font-serif text-2xl mb-4">Event details</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          {fields.map(([k, label, type]) => (
            <div key={k}><Label className="df-label">{label}</Label>
              {k === "service_hours" ? (
                <>
                  <Input type="number" className="df-input bg-clay" readOnly value={hoursFromTimes(f.service_start_time, f.service_end_time) ?? (f.service_hours ?? "")} data-testid="field-service_hours" />
                  <p className="text-[11px] text-muted-foreground mt-1">Auto from Service start/end times</p>
                </>
              ) : (
                <Input type={type || "text"} className="df-input" value={f[k] ?? ""} onChange={(e) => set(k, type === "number" ? (e.target.value === "" ? null : Number(e.target.value)) : e.target.value)} data-testid={`field-${k}`} />
              )}
            </div>
          ))}
        </div>
        <div className="mt-4"><Label className="df-label">Internal notes</Label><Textarea className="df-input" value={f.internal_notes ?? ""} onChange={(e) => set("internal_notes", e.target.value)} /></div>
        <Button onClick={() => patch(f, "Event saved")} className="btn-dark mt-5" data-testid="save-event"><Save className="h-4 w-4" /> Save Details</Button>
      </div>

      <div className="df-card p-6">
        <p className="df-label flex items-center gap-2"><Clock className="h-3 w-3" /> Timeline</p>
        <div className="flex gap-2 mb-4">
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add note…" className="df-input" data-testid="event-note-input" />
          <Button onClick={addNote} className="btn-dark" data-testid="event-add-note"><Plus className="h-4 w-4" /></Button>
        </div>
        <div className="space-y-3 max-h-[520px] overflow-y-auto">
          {(event.timeline || []).slice().reverse().map((t, i) => (
            <div key={i} className="flex gap-3 text-sm"><div className="h-2 w-2 rounded-full bg-olive mt-1.5 shrink-0" /><div><span>{t.text}</span><div className="text-xs text-muted-foreground">{new Date(t.ts).toLocaleString()}</div></div></div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ---------- QUOTE ---------- */
function QuoteTab({ event, pricing, transport, patch, trigger, openDoc, reload }) {
  const q = event.quote || {};
  const menuConfirmed = ["Menu confirmed", "Alcohol list generated"].includes(event.menu_selection_status);
  const autoHours = hoursFromTimes(event.service_start_time, event.service_end_time);
  const guests = Number(event.guest_count) || 0;
  const initHours = q.hours || event.service_hours || autoHours || 0;
  const [inp, setInp] = useState({
    bartenders: q.bartenders || 1,
    hours: initHours,
    rate: q.rate || pricing?.promo_bartender_rate || 55,
    regular_rate: q.regular_rate || pricing?.regular_bartender_rate || 75,
    mobile_bar_rental: q.mobile_bar_rental ?? 70,
    basic_supplies: q.basic_supplies ?? suppliesFormula(guests, initHours),
    menu_ingredients: q.menu_ingredients ?? suppliesFormula(guests, initHours),
    transportation: q.transportation ?? pricing?.transportation_default ?? 50,
    discount: q.discount ?? 0,
    hst_rate: q.hst_rate ?? pricing?.hst_rate ?? 13,
    deposit_pct: q.deposit_pct ?? pricing?.deposit_pct ?? 50,
  });
  const [basicOverride, setBasicOverride] = useState(!!q.basic_override);
  const [menuOverride, setMenuOverride] = useState(!!q.menu_override);
  const [addonItems, setAddonItems] = useState(q.addon_items || []);
  const [sendState, setSendState] = useState({ link: "", attachGuide: true, sending: false });

  useEffect(() => {
    if (pricing && !q.total) {
      setInp((p) => ({ ...p, rate: p.rate || pricing.promo_bartender_rate || 55, regular_rate: pricing.regular_bartender_rate || 75, hst_rate: pricing.hst_rate ?? 13, deposit_pct: pricing.deposit_pct ?? 50 }));
    }
    // eslint-disable-next-line
  }, [pricing]);

  // Auto-calc supplies & menu-specific ingredients: guests × hours × $1.25 (unless manually overridden)
  useEffect(() => {
    const f = suppliesFormula(guests, Number(inp.hours) || 0);
    setInp((p) => ({ ...p, basic_supplies: basicOverride ? p.basic_supplies : f, menu_ingredients: menuOverride ? p.menu_ingredients : f }));
    // eslint-disable-next-line
  }, [inp.hours, guests, basicOverride, menuOverride]);

  const recalcSupplies = () => {
    const f = suppliesFormula(guests, Number(inp.hours) || 0);
    setBasicOverride(false); setMenuOverride(false);
    setInp((p) => ({ ...p, basic_supplies: f, menu_ingredients: f }));
    toast.success("Recalculated supplies from formula");
  };

  const applyCityFee = () => {
    const c = transport?.cities?.find((x) => x.city === event.event_city);
    if (c) { setInp((p) => ({ ...p, transportation: c.fee })); toast.success(`Applied ${event.event_city} fee`); }
    else toast.info("No default fee for this city");
  };

  const computed = useMemo(() => computeQuote({ ...inp, addon_items: addonItems }, pricing || {}), [inp, addonItems, pricing]);
  const validation = useMemo(() => validateQuoteInputs(event, inp), [event, inp]);
  const set = (k, v) => setInp((p) => ({ ...p, [k]: v === "" ? "" : Number(v) }));
  const setAddon = (i, k, v) => setAddonItems((a) => a.map((it, j) => j === i ? { ...it, [k]: (k === "label" || k === "details") ? v : Number(v) } : it));
  const addAddon = (preset) => setAddonItems((a) => [...a, preset ? { ...preset } : { label: "New add-on", details: "", regular: 0, customer: 0 }]);
  const removeAddon = (i) => setAddonItems((a) => a.filter((_, j) => j !== i));

  const saveQuote = async () => {
    if (!validation.valid) { toast.error("Missing required event details. Please add guest count, service hours, bartender count, and service type before generating quote."); return; }
    await patch({ quote: { ...computed, basic_override: basicOverride, menu_override: menuOverride }, event_status: event.event_status === "Inquiry" ? "Quote sent" : event.event_status }, "Quote saved");
    await trigger("quote_generated");
  };

  const sendQuote = async () => {
    if (!validation.valid) { toast.error("Missing required event details. Please generate a valid quote first."); return; }
    setSendState((s) => ({ ...s, sending: true }));
    try {
      await patch({ quote: { ...computed, basic_override: basicOverride, menu_override: menuOverride } });
      const { data } = await api.post(`/events/${event.id}/send-quote`, { base_url: window.location.origin, attach_guide: sendState.attachGuide });
      setSendState((s) => ({ ...s, link: data.quote_link }));
      toast.success(`Quote emailed to ${data.to}${data.guide_link ? " with selection guide" : ""}`);
      if (reload) await reload();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Could not send quote"); }
    finally { setSendState((s) => ({ ...s, sending: false })); }
  };

  const copyQuoteLink = async () => {
    try {
      const { data } = await api.post(`/events/${event.id}/quote-link`);
      const url = `${window.location.origin}${data.path}`;
      setSendState((s) => ({ ...s, link: url }));
      try { await navigator.clipboard.writeText(url); toast.success("Quote link copied to clipboard"); }
      catch { toast.success("Quote link generated"); }
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Could not create link"); }
  };

  const rows = [
    ["bartenders", "Bartenders"], ["hours", "Service hours"], ["rate", "Bartender rate ($/hr)"],
    ["regular_rate", "Regular reference rate ($/hr)"], ["mobile_bar_rental", "Mobile bar rental ($)"],
    ["basic_supplies", "Basic supplies package ($)"], ["menu_ingredients", "Menu-specific ingredients ($)"],
    ["transportation", "Transportation ($)"], ["discount", "Additional discount ($)"],
    ["hst_rate", "HST rate (%)"], ["deposit_pct", "Deposit (%)"],
  ];

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="df-card p-6">
        <h3 className="font-serif text-2xl mb-1 flex items-center gap-2"><Calculator className="h-5 w-5 text-olive" /> Quote Calculator</h3>
        <p className="text-sm text-muted-foreground mb-4">Regular reference rate ($75/hr) is always shown so clients see their savings. Promo bartender rate defaults to $55/hr.</p>

        {!validation.valid && (
          <div className="rounded-xl bg-amber-50 border border-amber-200 text-amber-800 p-3 text-sm mb-4 flex gap-2" data-testid="quote-validation-warning">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            <span>Missing required event details. Please add guest count, service hours, bartender count, and service type before generating quote. <span className="font-medium">Missing: {validation.missing.join(", ")}.</span></span>
          </div>
        )}

        <div className="grid sm:grid-cols-2 gap-3">
          {rows.map(([k, label]) => (
            <div key={k}>
              <Label className="df-label">{label}</Label>
              <Input type="number" className="df-input" value={inp[k]} onChange={(e) => { if (k === "basic_supplies") setBasicOverride(true); if (k === "menu_ingredients") setMenuOverride(true); set(k, e.target.value); }} data-testid={`quote-input-${k}`} />
              {k === "basic_supplies" && <p className={`text-[11px] mt-1 ${basicOverride ? "text-amber-700" : "text-muted-foreground"}`} data-testid="basic-supplies-note">{basicOverride ? "Manual override active" : "Auto: guests × hours × $1.25"}</p>}
              {k === "menu_ingredients" && <p className={`text-[11px] mt-1 ${menuOverride ? "text-amber-700" : "text-muted-foreground"}`} data-testid="menu-ingredients-note">{menuOverride ? "Manual override active" : "Auto: guests × hours × $1.25"}</p>}
              {k === "menu_ingredients" && !menuConfirmed && <p className="text-[11px] text-amber-700 mt-0.5">Menu pending — final ingredient cost may be adjusted after cocktail selection.</p>}
              {k === "hours" && !inp.hours && autoHours ? <p className="text-[11px] text-muted-foreground mt-1">Auto from service time: {autoHours} hrs</p> : null}
            </div>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 mt-4">
          <button onClick={applyCityFee} className="btn-outline text-xs" data-testid="apply-city-fee">Apply {event.event_city || "city"} transport fee</button>
          <button onClick={recalcSupplies} className="btn-outline text-xs" data-testid="recalc-supplies">Recalculate from formula</button>
        </div>

        <div className="border-t border-line mt-5 pt-4">
          <div className="flex items-center justify-between mb-2">
            <p className="df-label mb-0">Add-ons (line items)</p>
            <select className="text-xs bg-clay rounded-lg px-2 py-1" value="" onChange={(e) => { const p = ADDON_PRESETS.find((x) => x.label === e.target.value); if (p) addAddon(p); }} data-testid="quote-addon-preset">
              <option value="">+ Add preset…</option>
              {ADDON_PRESETS.map((p) => <option key={p.label} value={p.label}>{`${p.label} ($${p.customer})`}</option>)}
            </select>
          </div>
          {addonItems.map((a, i) => (
            <div key={i} className="flex items-center gap-2 mb-2" data-testid={`quote-addon-${i}`}>
              <Input className="df-input flex-1 py-1.5" value={a.label} onChange={(e) => setAddon(i, "label", e.target.value)} placeholder="Add-on" />
              <Input type="number" className="df-input w-24 py-1.5" value={a.customer} onChange={(e) => setAddon(i, "customer", e.target.value)} placeholder="$" />
              <button onClick={() => removeAddon(i)} className="text-muted-foreground hover:text-destructive" data-testid={`quote-addon-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
          <button onClick={() => addAddon()} className="text-xs text-olive" data-testid="quote-addon-add"><Plus className="h-3 w-3 inline" /> Add custom add-on</button>
        </div>
      </div>

      <div className="df-card p-6 flex flex-col">
        <h3 className="font-serif text-2xl mb-4">Summary</h3>
        <div className="space-y-1 text-sm flex-1">
          <SummaryRow label="Bartender Service (regular)" value={CAD(computed.bartender_regular)} strike />
          <SummaryRow label="Bartender Service (customer)" value={CAD(computed.bartender_customer)} accent />
          <SummaryRow label="Mobile Bar Rental" value={CAD(computed.mobile_bar_rental)} />
          <SummaryRow label="Basic Supplies Package" value={CAD(computed.basic_supplies)} />
          <SummaryRow label="Menu-Specific Ingredients" value={CAD(computed.menu_ingredients)} />
          <SummaryRow label="Transportation" value={CAD(computed.transportation)} />
          <SummaryRow label="Add-ons" value={CAD(computed.addons_total)} />
          {computed.discount > 0 && <SummaryRow label="Additional discount" value={"– " + CAD(computed.discount)} />}
          <div className="h-px bg-line my-2" />
          <SummaryRow label="Regular Price" value={CAD(computed.regular_price)} />
          <SummaryRow label="Promotional Savings" value={"– " + CAD(computed.savings)} accent />
          <SummaryRow label="Subtotal Before HST" value={CAD(computed.subtotal)} />
          <SummaryRow label={`HST (${computed.hst_rate}%)`} value={CAD(computed.hst)} />
          <div className="flex justify-between py-2 border-t border-ink/15 font-semibold text-xl"><span>Final Total</span><span data-testid="quote-total">{CAD(computed.total)}</span></div>
          <SummaryRow label="50% Deposit" value={CAD(computed.deposit)} accent />
          <SummaryRow label="Remaining Balance" value={CAD(computed.balance)} />
        </div>
        <div className="flex flex-wrap gap-2 mt-5">
          <Button onClick={saveQuote} className="btn-dark flex-1" data-testid="save-quote"><Save className="h-4 w-4" /> Save Quote</Button>
          <Button onClick={openDoc} className="btn-outline flex-1" data-testid="preview-quote"><FileText className="h-4 w-4" /> Preview / PDF</Button>
        </div>

        <div className="border-t border-line mt-5 pt-4">
          <p className="df-label flex items-center gap-2"><Send className="h-3 w-3" /> Send quote to client</p>
          <label className="flex items-center gap-2 text-sm mb-3"><Checkbox checked={sendState.attachGuide} onCheckedChange={(v) => setSendState((s) => ({ ...s, attachGuide: !!v }))} data-testid="attach-guide-toggle" /> Attach Cocktail &amp; Mocktail Selection Guide</label>
          <div className="flex flex-wrap gap-2">
            <Button onClick={sendQuote} disabled={sendState.sending} className="btn-dark" data-testid="send-quote"><Mail className="h-4 w-4" /> {sendState.sending ? "Sending…" : "Send Quote + Cocktail Selection Guide"}</Button>
            <Button onClick={copyQuoteLink} className="btn-outline" data-testid="copy-quote-link"><Link2 className="h-4 w-4" /> Copy Quote Link</Button>
          </div>
          {sendState.link && (
            <div className="mt-3 flex items-center gap-2 bg-clay rounded-lg px-3 py-2 text-sm" data-testid="quote-link-box">
              <span className="truncate flex-1 text-muted-foreground">{sendState.link}</span>
              <button onClick={() => { navigator.clipboard.writeText(sendState.link); toast.success("Copied"); }} className="text-olive shrink-0"><Copy className="h-4 w-4" /></button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
const SummaryRow = ({ label, value, strike, accent }) => (
  <div className="flex justify-between py-1"><span className="text-muted-foreground">{label}</span><span className={`${strike ? "line-through text-muted-foreground" : ""} ${accent ? "text-olive font-medium" : ""}`}>{value}</span></div>
);

/* ---------- DOCUMENTS ---------- */
function DocumentsTab({ event, patch, trigger, setDoc, reload }) {
  const [signLink, setSignLink] = useState("");
  const [emailingLink, setEmailingLink] = useState(false);
  const sig = event.contract?.signature;

  const getSignLink = async () => {
    try {
      const { data } = await api.post(`/events/${event.id}/signing-link`);
      const url = `${window.location.origin}${data.path}`;
      setSignLink(url);
      try { await navigator.clipboard.writeText(url); toast.success("Signing link copied to clipboard"); }
      catch { toast.success("Signing link generated"); }
      if (reload) await reload();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Could not generate link"); }
  };

  const emailSignLink = async () => {
    setEmailingLink(true);
    try {
      const { data } = await api.post(`/events/${event.id}/email-signing-link`, { base_url: window.location.origin });
      setSignLink(data.sign_url);
      toast.success(`Signing link emailed to ${data.to}`);
      if (reload) await reload();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Could not email link"); }
    finally { setEmailingLink(false); }
  };

  const sendReminder = async () => {
    try { const { data } = await api.post(`/events/${event.id}/send-reminder`); toast.success(`Reminder sent to ${data.to}`); if (reload) await reload(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || "Failed to send reminder"); }
  };

  const markDepositPaid = async () => {
    try {
      const { data } = await api.post(`/events/${event.id}/mark-deposit-paid`, { base_url: window.location.origin });
      const c = data.confirmation || {};
      if (c.status === "sent") toast.success("Deposit confirmed — confirmation package sent to client");
      else if (c.status === "skipped") toast.success("Deposit confirmed (no client email on file to send confirmation)");
      else toast.warning("Deposit confirmed, but the confirmation email could not be sent — try re-sending it");
      if (reload) await reload();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Could not mark deposit paid"); }
  };

  const sendConfirmation = async () => {
    try {
      const { data } = await api.post(`/events/${event.id}/send-confirmation`, { base_url: window.location.origin });
      toast.success(`Confirmation package sent to ${data.to}`);
      if (reload) await reload();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Could not send confirmation package"); }
  };
  const docs = [
    { type: "quote", label: "Quote", icon: FileText, action: "quote_generated" },
    { type: "contract", label: "Contract", icon: ScrollText, action: "contract_generated", statusKey: "contract_status", statusVal: "Generated" },
    { type: "invoice", label: "Invoice", icon: Receipt, action: "invoice_generated" },
  ];
  const inv = event.invoice || {};
  return (
    <div className="space-y-6">
      <div className="grid sm:grid-cols-3 gap-4">
        {docs.map((d) => (
          <div key={d.type} className="df-card p-6">
            <d.icon className="h-6 w-6 text-olive mb-3" />
            <h3 className="font-serif text-2xl mb-1">{d.label}</h3>
            <p className="text-sm text-muted-foreground mb-4">Generate a premium {d.label.toLowerCase()} with your logo, ready to print or save as PDF.</p>
            <div className="flex flex-col gap-2">
              <Button onClick={() => setDoc(d.type)} className="btn-dark" data-testid={`open-${d.type}`}>Open {d.label}</Button>
              <button onClick={() => { if (d.statusKey) patch({ [d.statusKey]: d.statusVal }); trigger(d.action); }} className="text-xs text-olive" data-testid={`gen-webhook-${d.type}`}><Zap className="h-3 w-3 inline" /> Mark generated + automate</button>
            </div>
          </div>
        ))}
      </div>

      <div className="df-card p-6" data-testid="esignature-card">
        <h3 className="font-serif text-2xl mb-2 flex items-center gap-2"><PenLine className="h-5 w-5 text-olive" /> Client E-Signature</h3>
        {sig ? (
          <div data-testid="signature-status">
            <div className="flex flex-wrap items-center gap-3 mb-3">
              <Badge variant="outline" className={statusTone("signed")} data-testid="contract-signed-badge">Signed</Badge>
              <span className="text-sm text-muted-foreground">by <strong className="text-ink">{sig.legal_name}</strong> · {new Date(sig.signed_at).toLocaleString()}{sig.ip ? ` · IP ${sig.ip}` : ""}</span>
            </div>
            <img src={sig.signature_data} alt="Client signature" className="h-16 border border-line rounded-lg bg-white px-2" />
            <div className="mt-3"><Button onClick={() => setDoc("contract")} className="btn-outline" data-testid="view-signed-contract"><ScrollText className="h-4 w-4" /> View Signed Contract</Button></div>
          </div>
        ) : (
          <>
            <p className="text-sm text-muted-foreground mb-4">Send your client a secure link to review and sign the service agreement online. Once signed, the contract status flips to <strong>Signed</strong> automatically.</p>
            <div className="flex flex-wrap gap-2">
              <Button onClick={getSignLink} className="btn-dark" data-testid="get-signing-link"><Link2 className="h-4 w-4" /> Get &amp; Copy Signing Link</Button>
              <Button onClick={emailSignLink} disabled={emailingLink} className="btn-outline" data-testid="email-signing-link"><Mail className="h-4 w-4" /> {emailingLink ? "Sending…" : "Email Signing Link to Client"}</Button>
            </div>
            {signLink && (
              <div className="mt-4 flex items-center gap-2 bg-clay rounded-lg px-3 py-2 text-sm" data-testid="signing-link-box">
                <span className="truncate flex-1 text-muted-foreground">{signLink}</span>
                <button onClick={() => { navigator.clipboard.writeText(signLink); toast.success("Copied"); }} className="text-olive shrink-0" data-testid="copy-signing-link"><Copy className="h-4 w-4" /></button>
              </div>
            )}
          </>
        )}
      </div>

      <div className="df-card p-6">
        <h3 className="font-serif text-2xl mb-4 flex items-center gap-2"><Receipt className="h-5 w-5 text-olive" /> Invoice & Payment</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label className="df-label">Square payment link</Label><Input className="df-input" placeholder="https://square.link/…" defaultValue={inv.square_link} onBlur={(e) => patch({ invoice: { ...inv, square_link: e.target.value } })} data-testid="square-link" /></div>
          <div><Label className="df-label">Deposit paid ($)</Label><Input type="number" className="df-input" defaultValue={inv.deposit_paid || 0} onBlur={(e) => patch({ invoice: { ...inv, deposit_paid: Number(e.target.value) } })} data-testid="deposit-paid" /></div>
        </div>
        <div className="flex flex-wrap gap-2 mt-4">
          {inv.square_link && <Button onClick={() => { window.open(inv.square_link, "_blank"); patch({ payment_status: "Pending confirmation" }, "Marked pending confirmation"); }} className="btn-outline" data-testid="pay-deposit"><CreditCard className="h-4 w-4" /> Pay Deposit (Square)</Button>}
          <Button onClick={markDepositPaid} className="btn-dark" data-testid="mark-deposit-paid"><CheckCircle2 className="h-4 w-4" /> Confirm Deposit Paid</Button>
          <Button onClick={() => { patch({ payment_status: "Fully paid" }, "Balance marked paid"); trigger("balance_marked_paid"); }} className="btn-outline" data-testid="mark-balance-paid"><CheckCircle2 className="h-4 w-4" /> Mark Fully Paid</Button>
          <Button onClick={sendConfirmation} className="btn-outline" data-testid="send-confirmation-pkg"><Send className="h-4 w-4" /> Send Confirmation Package</Button>
        </div>
        <p className="text-xs text-muted-foreground mt-3">Confirming the 50% deposit automatically emails the client their booking confirmation package (documents + payment summary). Use “Send Confirmation Package” to re-send it manually.</p>
        {!event.contract?.signature && <p className="text-xs text-amber-700 mt-2 flex items-center gap-1" data-testid="admin-sign-first-note"><AlertTriangle className="h-3 w-3" /> Client hasn't signed yet — the client-facing payment link stays locked until the contract is signed.</p>}
        {event.payment_status === "Pending confirmation" && <p className="text-xs text-amber-700 mt-2">Client tapped "Pay Deposit" — confirm once the Square payment is received.</p>}
      </div>

      <div className="df-card p-6">
        <h3 className="font-serif text-2xl mb-2 flex items-center gap-2"><BellRing className="h-5 w-5 text-olive" /> Final Reminder</h3>
        <p className="text-sm text-muted-foreground mb-4">Reminders send automatically a few days before confirmed events (configure in Settings → Reminders). You can also send one now.</p>
        <div className="flex items-center gap-3">
          <Button onClick={sendReminder} className="btn-dark" data-testid="send-reminder-now"><Send className="h-4 w-4" /> Send Reminder Now</Button>
          {event.reminder_sent && <Badge variant="outline" className={statusTone("sent")}>Reminder sent</Badge>}
        </div>
      </div>
    </div>
  );
}

/* ---------- MENU ---------- */
const ChoiceRow = ({ label, value }) => value ? (
  <div className="flex gap-2 text-sm py-1.5 border-b border-line/60 last:border-0"><span className="text-muted-foreground w-44 shrink-0">{label}</span><span className="text-ink">{value}</span></div>
) : null;

function ChoiceView({ cc }) {
  const extras = cc.extras || {};
  const labels = {
    smoked_cocktail: "Smoked cocktail presentation", personalized_cups: "Personalized cups",
    custom_menu_sign: "Custom menu sign", kids_lemonade_bar: "Kids lemonade bar",
    premium_garnish: "Premium garnish design", beverage_serving: "Beer/wine/champagne/prosecco serving",
  };
  const extrasList = Object.keys(labels).filter((k) => extras[k]).map((k) => labels[k]);
  if (extras.other) extrasList.push(extras.other);
  const isHelp = cc.mode === "help";
  return (
    <div>
      {isHelp ? (
        <>
          <ChoiceRow label="Needs help choosing" value="Yes — Drunk Fruit to build the menu" />
          <ChoiceRow label="Preferred flavors" value={(cc.preferred_flavors || []).join(", ")} />
          <ChoiceRow label="Alcohol preferences" value={(cc.alcohol_preferences || []).join(", ")} />
          <ChoiceRow label="Event style / theme" value={cc.event_style} />
          <ChoiceRow label="Guest preferences" value={cc.guest_preferences} />
        </>
      ) : (
        <>
          <ChoiceRow label="Signature drink 1" value={cc.drink1} />
          <ChoiceRow label="Signature drink 2" value={cc.drink2} />
          <ChoiceRow label="Signature drink 3" value={cc.drink3} />
          <ChoiceRow label="Mocktail version" value={cc.mocktail_needed ? "Yes" : "No"} />
        </>
      )}
      <ChoiceRow label="Kids drinks" value={cc.kids_needed ? "Yes" : "No"} />
      <ChoiceRow label="Allergies / restrictions" value={cc.allergies} />
      <ChoiceRow label="Extras requested" value={extrasList.join(", ")} />
      <ChoiceRow label="Notes" value={cc.notes} />
    </div>
  );
}

function MenuTab({ event, patch, trigger, setDoc }) {
  const [menu, setMenu] = useState(event.menu || {});
  const [kids, setKids] = useState(event.kids_menu || {});
  useEffect(() => { setMenu(event.menu || {}); setKids(event.kids_menu || {}); }, [event.id]);
  const sel = event.selected_cocktails || [];
  const setC = (idx, k, v) => setMenu((m) => ({ ...m, [idx]: { ...(m[idx] || {}), [k]: v } }));
  const setK = (idx, k, v) => setKids((m) => ({ ...m, [idx]: { ...(m[idx] || {}), [k]: v } }));
  const namesFromMenu = () => ["c1", "c2", "c3"].map((c) => menu[c]?.name).filter(Boolean);
  const confirmMenu = () => {
    const names = namesFromMenu();
    patch({ selected_cocktails: names.length ? names : (event.selected_cocktails || []), menu_status: "Confirmed", menu_selection_status: "Menu confirmed" }, "Menu confirmed — alcohol list unlocked");
  };

  return (
    <div className="space-y-6">
      <div className="df-card p-6" data-testid="menu-selection-workflow">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="font-serif text-2xl flex items-center gap-2"><ListChecks className="h-5 w-5 text-olive" /> Menu Selection</h3>
            <p className="text-sm text-muted-foreground mt-1">Track the client's cocktail/mocktail selection. The client alcohol list unlocks once the menu is <strong>confirmed</strong>.</p>
          </div>
          <Badge variant="outline" className={statusTone(event.menu_selection_status || "Not sent")} data-testid="menu-selection-status-badge">{event.menu_selection_status || "Not sent"}</Badge>
        </div>
        <div className="flex flex-wrap gap-2 mt-4">
          <Button onClick={() => patch({ menu_selection_status: "Waiting for client choices" }, "Marked waiting")} className="btn-outline text-xs" data-testid="menu-mark-waiting">Waiting for choices</Button>
          <Button onClick={() => patch({ menu_selection_status: "Client choices received" }, "Marked received")} className="btn-outline text-xs" data-testid="menu-mark-received">Choices received</Button>
          <Button onClick={confirmMenu} className="btn-dark text-xs" data-testid="menu-confirm">Confirm Menu</Button>
        </div>
      </div>

      {event.client_choices && (
        <div className="df-card p-6" data-testid="client-drink-choices">
          <div className="flex items-center justify-between gap-3 mb-3">
            <h3 className="font-serif text-2xl flex items-center gap-2"><Wine className="h-5 w-5 text-olive" /> Client Drink Choices</h3>
            <Badge variant="outline" className="bg-olive/10 text-olive border-olive/30">Received</Badge>
          </div>
          <ChoiceView cc={event.client_choices} />
          <Button onClick={confirmMenu} className="btn-dark text-xs mt-4" data-testid="choices-confirm-menu"><CheckCircle2 className="h-3 w-3" /> Mark Menu Confirmed</Button>
        </div>
      )}
      <div className="grid lg:grid-cols-2 gap-6">
      <div className="df-card p-6">
        <h3 className="font-serif text-2xl mb-4 flex items-center gap-2"><Utensils className="h-5 w-5 text-olive" /> Custom Menu Builder</h3>
        <div className="grid sm:grid-cols-2 gap-3 mb-4">
          <div><Label className="df-label">Menu title</Label><Input className="df-input" value={menu.title || ""} onChange={(e) => setMenu({ ...menu, title: e.target.value })} data-testid="menu-title" /></div>
          <div><Label className="df-label">Theme colors</Label><Input className="df-input" value={menu.theme_colors || ""} onChange={(e) => setMenu({ ...menu, theme_colors: e.target.value })} /></div>
        </div>
        {["c1", "c2", "c3"].map((c, i) => (
          <div key={c} className="border-t border-line pt-3 mt-3">
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Cocktail {i + 1}</p>
            <Input className="df-input mb-2" placeholder={sel[i] || "Name"} value={menu[c]?.name || ""} onChange={(e) => setC(c, "name", e.target.value)} data-testid={`menu-c${i + 1}-name`} />
            <Textarea className="df-input mb-2" placeholder="Ingredients" value={menu[c]?.ingredients || ""} onChange={(e) => setC(c, "ingredients", e.target.value)} />
            <label className="flex items-center gap-2 text-sm"><Checkbox checked={!!menu[c]?.mocktail} onCheckedChange={(v) => setC(c, "mocktail", !!v)} /> Mocktail version available</label>
          </div>
        ))}
        <div className="mt-4"><Label className="df-label">Beer/wine/champagne note</Label><Input className="df-input" value={menu.beverage_note || ""} onChange={(e) => setMenu({ ...menu, beverage_note: e.target.value })} /></div>
        <div className="mt-3"><Label className="df-label">Allergy note</Label><Input className="df-input" value={menu.allergy_note || ""} onChange={(e) => setMenu({ ...menu, allergy_note: e.target.value })} /></div>
        <div className="flex gap-2 mt-5">
          <Button onClick={() => { const names = namesFromMenu(); patch({ menu: { ...menu, event_name: event.full_name }, menu_status: "Generated", selected_cocktails: names.length ? names : (event.selected_cocktails || []) }, "Menu saved"); trigger("menu_generated"); }} className="btn-dark flex-1" data-testid="save-menu"><Save className="h-4 w-4" /> Save</Button>
          <Button onClick={() => setDoc("menu")} className="btn-outline flex-1" data-testid="preview-menu"><FileText className="h-4 w-4" /> Preview</Button>
        </div>
      </div>

      <div className="df-card p-6">
        <h3 className="font-serif text-2xl mb-4 flex items-center gap-2"><Baby className="h-5 w-5 text-olive" /> Kids Menu Builder</h3>
        <div className="grid sm:grid-cols-2 gap-3 mb-4">
          <div><Label className="df-label">Menu title</Label><Input className="df-input" value={kids.title || ""} onChange={(e) => setKids({ ...kids, title: e.target.value })} data-testid="kids-title" /></div>
          <div><Label className="df-label">Theme colors</Label><Input className="df-input" value={kids.theme_colors || ""} onChange={(e) => setKids({ ...kids, theme_colors: e.target.value })} /></div>
        </div>
        {["drink1", "drink2", "drink3"].map((c, i) => (
          <div key={c} className="border-t border-line pt-3 mt-3">
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Drink {i + 1}</p>
            <Input className="df-input mb-2" placeholder="Name" value={kids[c]?.name || ""} onChange={(e) => setK(c, "name", e.target.value)} data-testid={`kids-d${i + 1}-name`} />
            <Textarea className="df-input" placeholder="Ingredients" value={kids[c]?.ingredients || ""} onChange={(e) => setK(c, "ingredients", e.target.value)} />
          </div>
        ))}
        <div className="mt-4"><Label className="df-label">Notes</Label><Input className="df-input" value={kids.notes || ""} onChange={(e) => setKids({ ...kids, notes: e.target.value })} /></div>
        <div className="flex gap-2 mt-5">
          <Button onClick={() => patch({ kids_menu: { ...kids, event_name: event.full_name } }, "Kids menu saved")} className="btn-dark flex-1" data-testid="save-kids-menu"><Save className="h-4 w-4" /> Save</Button>
          <Button onClick={() => setDoc("kids_menu")} className="btn-outline flex-1" data-testid="preview-kids-menu"><FileText className="h-4 w-4" /> Preview</Button>
        </div>
      </div>
      </div>
    </div>
  );
}

/* ---------- ALCOHOL & SUPPLIES ---------- */
function AlcoholSuppliesTab({ event, patch, trigger, setDoc }) {
  const menuConfirmed = ["Menu confirmed", "Alcohol list generated"].includes(event.menu_selection_status);
  const applicability = alcoholListApplicability(event);
  const [override, setOverride] = useState(!!event.alcohol_list_override);
  const showList = applicability.applicable || override;
  const [cats, setCats] = useState(() => event.alcohol_list?.categories?.length ? event.alcohol_list.categories : generateClientAlcoholList(event));
  const [internal] = useState(() => event.internal_supplies?.categories?.length ? event.internal_supplies.categories : generateInternalSupplies(event));
  const [supplies, setSupplies] = useState(() => event.supplies?.items?.length ? event.supplies.items : DEFAULT_SUPPLIES.map((name) => ({ name, checked: false })));
  const [newSupply, setNewSupply] = useState("");

  const regenAlcohol = () => { setCats(generateClientAlcoholList(event)); toast.success("Alcohol list generated from confirmed menu"); };
  const setQty = (ci, ii, qty) => setCats((cs) => cs.map((c, i) => i !== ci ? c : { ...c, items: c.items.map((it, j) => j !== ii ? it : { ...it, qty }) }));
  const toggleSupply = (i) => setSupplies((s) => s.map((it, j) => j === i ? { ...it, checked: !it.checked } : it));
  const addSupply = () => { if (newSupply.trim()) { setSupplies((s) => [...s, { name: newSupply, checked: false }]); setNewSupply(""); } };
  const saveAlcohol = () => {
    patch({ alcohol_list: { categories: cats }, alcohol_list_status: "Generated", menu_selection_status: "Alcohol list generated" }, "Alcohol list saved");
    trigger("alcohol_list_generated");
  };

  return (
    <div className="space-y-6">
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="df-card p-6">
          <h3 className="font-serif text-2xl mb-2 flex items-center gap-2"><Wine className="h-5 w-5 text-olive" /> Client Alcohol List</h3>
          <div className="rounded-xl bg-clay p-3 text-xs text-muted-foreground mb-4">{ALCOHOL_DISCLAIMER}</div>
          {!showList ? (
            <div className="rounded-xl bg-clay/60 border border-line p-4 text-sm" data-testid="alcohol-not-applicable">
              <p className="text-muted-foreground mb-3 flex gap-2"><AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-amber-600" /><span>{applicability.reason}</span></p>
              <Button onClick={() => { setOverride(true); patch({ alcohol_list_override: true }); }} className="btn-outline" data-testid="alcohol-override"><Wine className="h-4 w-4" /> Generate client alcohol list anyway</Button>
            </div>
          ) : !menuConfirmed ? (
            <div className="rounded-xl bg-amber-50 border border-amber-200 text-amber-800 p-4 text-sm flex gap-2" data-testid="alcohol-menu-pending">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>Menu pending — alcohol list will be generated after the client selects the final cocktails/mocktails. Confirm the menu in the <strong>Menu</strong> tab to unlock this list.</span>
            </div>
          ) : (
            <>
              {override && !applicability.applicable && (
                <div className="flex items-center justify-between text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2 mb-3" data-testid="alcohol-override-active">
                  <span>Admin override active — client alcohol list enabled for this service type.</span>
                  <button onClick={() => { setOverride(false); patch({ alcohol_list_override: false }); }} className="text-olive underline shrink-0 ml-2">Remove</button>
                </div>
              )}
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs text-muted-foreground">Alcohol only — generated from the confirmed cocktail/mocktail selection.</p>
                <button onClick={regenAlcohol} className="text-xs text-olive" data-testid="regen-alcohol">Regenerate from menu</button>
              </div>
              <div className="max-h-[360px] overflow-y-auto space-y-4 pr-1">
                {cats.length === 0 && <p className="text-sm text-muted-foreground">No alcoholic drinks in the selected menu (mocktail-only). No client alcohol list required.</p>}
                {cats.map((c, ci) => (
                  <div key={c.name}>
                    <p className="text-xs uppercase tracking-widest text-olive mb-1">{c.name}</p>
                    {c.items.map((it, ii) => (
                      <div key={ii} className="flex items-center gap-3 py-1">
                        <span className="text-sm flex-1">{it.item}</span>
                        <Input type="number" className="df-input max-w-[80px] py-1.5" value={it.qty} onChange={(e) => setQty(ci, ii, Number(e.target.value))} />
                      </div>
                    ))}
                  </div>
                ))}
              </div>
              <div className="flex gap-2 mt-5">
                <Button onClick={saveAlcohol} className="btn-dark flex-1" data-testid="save-alcohol"><Save className="h-4 w-4" /> Save &amp; Generate</Button>
                <Button onClick={() => setDoc("alcohol_list")} className="btn-outline flex-1" data-testid="preview-alcohol"><FileText className="h-4 w-4" /> Preview</Button>
              </div>
            </>
          )}
        </div>

        <div className="df-card p-6">
          <h3 className="font-serif text-2xl mb-2 flex items-center gap-2"><Beer className="h-5 w-5 text-olive" /> Internal Ingredients &amp; Supplies</h3>
          <p className="text-xs text-muted-foreground mb-4">Mixers, citrus, syrups, juices, garnishes, cups, ice and tools — <strong>internal only, never shown to the client</strong>.</p>
          <div className="max-h-[420px] overflow-y-auto space-y-4 pr-1" data-testid="internal-supplies">
            {internal.map((c) => (
              <div key={c.name}>
                <p className="text-xs uppercase tracking-widest text-olive mb-1">{c.name}</p>
                {c.items.map((it, ii) => (
                  <div key={ii} className="flex items-center justify-between py-1 text-sm"><span>{it.item}</span><span className="text-muted-foreground">×{it.qty}</span></div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="df-card p-6">
        <h3 className="font-serif text-2xl mb-4 flex items-center gap-2"><PackageCheck className="h-5 w-5 text-olive" /> Supplies Checklist</h3>
        <div className="flex gap-2 mb-4">
          <Input value={newSupply} onChange={(e) => setNewSupply(e.target.value)} placeholder="Add custom item…" className="df-input" data-testid="add-supply-input" />
          <Button onClick={addSupply} className="btn-dark"><Plus className="h-4 w-4" /></Button>
        </div>
        <div className="max-h-[360px] overflow-y-auto grid sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-1">
          {supplies.map((it, i) => (
            <label key={i} className="flex items-center gap-3 py-1.5 text-sm cursor-pointer" data-testid={`supply-${i}`}>
              <Checkbox checked={it.checked} onCheckedChange={() => toggleSupply(i)} />
              <span className={it.checked ? "line-through text-muted-foreground" : ""}>{it.name}</span>
            </label>
          ))}
        </div>
        <div className="flex gap-2 mt-5">
          <Button onClick={() => patch({ supplies: { items: supplies }, supplies_status: "Created" }, "Supplies saved")} className="btn-dark" data-testid="save-supplies"><Save className="h-4 w-4" /> Save Supplies</Button>
          <Button onClick={() => setDoc("supplies")} className="btn-outline" data-testid="preview-supplies"><FileText className="h-4 w-4" /> Preview</Button>
        </div>
      </div>
    </div>
  );
}

const DEFAULT_SUPPLIES = [
  "Mobile bar", "Table", "Tablecloth", "Ice coolers", "Ice", "12 oz clear plastic cups", "Straws", "Napkins",
  "Cocktail shakers", "Jiggers", "Bar spoons", "Muddlers", "Pour spouts", "Cutting board", "Knife", "Garnish trays",
  "Rimmers", "Salt", "Sugar", "Chamoy", "Tajín", "Syrups", "Juices", "Mixers", "Fruit/garnishes", "Trash bags",
  "Cleaning towels", "Hand sanitizer", "Gloves", "Printed menu", "Stickers/custom cups", "Lights/decor",
  "Extension cord", "Payment reader", "Extra water", "Backup supplies",
];

/* ---------- CHECKLIST ---------- */
function ChecklistTab({ event, patch }) {
  const checklist = event.checklist || {};
  const items = Object.keys(checklist);
  const done = items.filter((k) => checklist[k]).length;
  const toggle = (k) => patch({ checklist: { ...checklist, [k]: !checklist[k] } });
  return (
    <div className="df-card p-6">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3 flex-wrap">
          <h3 className="font-serif text-2xl">Event Checklist</h3>
          <Badge variant="outline" className={statusTone(event.menu_selection_status || "Not sent")} data-testid="checklist-menu-status">Menu: {event.menu_selection_status || "Not sent"}</Badge>
        </div>
        <span className="text-sm text-muted-foreground">{done}/{items.length} complete</span>
      </div>
      <div className="h-2 rounded-full bg-clay overflow-hidden mb-6"><div className="h-full bg-olive rounded-full transition-all" style={{ width: `${items.length ? (done / items.length) * 100 : 0}%` }} /></div>
      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1">
        {items.map((k) => (
          <label key={k} className="flex items-center gap-3 py-2 text-sm cursor-pointer border-b border-line/50" data-testid={`checklist-${k}`}>
            <Checkbox checked={checklist[k]} onCheckedChange={() => toggle(k)} />
            <span className={checklist[k] ? "line-through text-muted-foreground" : ""}>{k}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

/* ---------- COMMS ---------- */
function CommsTab({ event, templates, emailConfigured }) {
  const [selected, setSelected] = useState(null);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  const pick = (t) => {
    setSelected(t);
    setSubject(renderTemplate(t.subject, event));
    setBody(renderTemplate(t.body, event));
  };

  const copy = async () => { await navigator.clipboard.writeText(`Subject: ${subject}\n\n${body}`); toast.success("Copied to clipboard"); };

  const sendEmail = async () => {
    setSending(true);
    try {
      const html = `<div style="font-family:Arial,sans-serif;color:#1A1A1A;line-height:1.6">${body.replace(/\n/g, "<br/>")}<hr style="border:none;border-top:1px solid #E5E0D8;margin:24px 0"/><p style="font-size:12px;color:#888">Drunk Fruit Mobile Bar · events@drunkfruit-ca.com · @Drunk.fruit</p></div>`;
      await api.post("/send-email", { recipient_email: event.email, subject, html_content: html });
      toast.success(`Email sent to ${event.email}`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Send failed"); }
    finally { setSending(false); }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="df-card p-4 max-h-[600px] overflow-y-auto">
        <p className="df-label mb-2">Templates</p>
        {templates.map((t) => (
          <button key={t.id} onClick={() => pick(t)} className={`w-full text-left px-3 py-2 rounded-lg text-sm mb-1 ${selected?.id === t.id ? "bg-ink text-white" : "hover:bg-clay"}`} data-testid={`template-${t.key}`}>{t.name}</button>
        ))}
      </div>
      <div className="lg:col-span-2 df-card p-6">
        {!selected ? <p className="text-sm text-muted-foreground py-16 text-center">Select a template to compose a message.</p> : (
          <>
            <div className="mb-3"><Label className="df-label">To</Label><Input className="df-input" value={event.email} readOnly data-testid="comms-to" /></div>
            <div className="mb-3"><Label className="df-label">Subject</Label><Input className="df-input" value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="comms-subject" /></div>
            <div className="mb-4"><Label className="df-label">Message</Label><Textarea className="df-input min-h-[240px]" value={body} onChange={(e) => setBody(e.target.value)} data-testid="comms-body" /></div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={copy} className="btn-outline" data-testid="comms-copy">Copy</Button>
              <a href={`mailto:${event.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`} className="btn-outline">Open in Mail</a>
              {emailConfigured && <Button onClick={sendEmail} disabled={sending} className="btn-dark" data-testid="comms-send"><Send className="h-4 w-4" /> {sending ? "Sending…" : "Send Email"}</Button>}
            </div>
            {!emailConfigured && <p className="text-xs text-muted-foreground mt-3">Email sending not configured — use Copy or Open in Mail.</p>}
          </>
        )}
      </div>
    </div>
  );
}
